// src/pages/Login.jsx
import React, { useState } from "react";
import { FaEnvelope, FaLock, FaEye, FaEyeSlash, FaPhone } from "react-icons/fa";
import { useNavigate, useLocation } from "react-router-dom";
import { jwtDecode } from "jwt-decode";
import vyapakprintsLogo from "../assets/vyapakprints-logo.png";
import { clearAuth } from "../auth";

const API_BASE = "https://api.vyapakprint.com/api";
const LOGIN_API = `${API_BASE}/distribution/login`;

const Login = () => {
  const [loginType, setLoginType] = useState("email");
  const [emailOrMobile, setEmailOrMobile] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();

  const redirectTo = location.state?.from?.pathname || "/";

  const buildPayload = () => {
    const value = String(emailOrMobile || "").trim();

    if (loginType === "mobile") {
      return {
        mobile: value.replace(/\D/g, ""),
        password,
      };
    }

    return {
      email: value.toLowerCase(),
      password,
    };
  };

  const extractUserFromResponse = (data, typedValue) => {
    const distribution = data?.distribution || {};
    const token = data?.token || "";

    let decoded = null;
    try {
      if (token) decoded = jwtDecode(token);
    } catch (decodeError) {
      console.error("Token decode failed:", decodeError);
    }

    return {
      id:
        distribution?._id ||
        distribution?.id ||
        decoded?.id ||
        decoded?._id ||
        decoded?.userId ||
        decoded?.sub ||
        null,
      distributionId:
        distribution?._id ||
        distribution?.id ||
        decoded?.distributionId ||
        null,
      email:
        distribution?.email ||
        decoded?.email ||
        (loginType === "email" ? typedValue.toLowerCase() : ""),
      mobile:
        distribution?.mobile ||
        decoded?.mobile ||
        (loginType === "mobile" ? typedValue.replace(/\D/g, "") : ""),
      businessName: distribution?.businessName || "",
      contactPerson: distribution?.contactPerson || "",
      status: distribution?.status || decoded?.status || "",
      role: distribution?.role || decoded?.role || "distribution",
      wallet: Number(distribution?.wallet || 0),
      userType:
        distribution?.userType?._id ||
        distribution?.userType ||
        decoded?.userType ||
        "",
    };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    try {
      const typedValue = String(emailOrMobile || "").trim();

      if (!typedValue) {
        throw new Error(
          loginType === "email"
            ? "Please enter your email."
            : "Please enter your mobile number.",
        );
      }

      if (!password) {
        throw new Error("Please enter your password.");
      }

      const response = await fetch(LOGIN_API, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
        body: JSON.stringify(buildPayload()),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data?.message || "Login failed.");
      }

      clearAuth();

      if (!data?.token) {
        throw new Error("Token not received from server.");
      }

      localStorage.setItem("token", data.token);
      localStorage.setItem("distributionToken", data.token);

      const safeUser = extractUserFromResponse(data, typedValue);
      localStorage.setItem("distributionUser", JSON.stringify(safeUser));
      localStorage.setItem("user", JSON.stringify(safeUser));

      navigate(redirectTo, { replace: true });
    } catch (err) {
      setError(err?.message || "Something went wrong!");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen flex-col bg-blue-800 text-gray-100 md:flex-row">
      <div className="flex w-full flex-col items-center justify-center bg-white p-8 md:w-1/2">
        <div className="mb-8 flex flex-col items-center">
          <img
            src={vyapakprintsLogo}
            alt="Vyapak Prints Logo"
            className="mb-4 w-full  object-contain"
          />
        </div>

        <div className="mt-4 text-center text-black">
          <p className="text-center text-gray-600 text-sm">
            © {new Date().getFullYear()} Developed by{" "}
            <span className="text-blue-600 font-semibold">Soft Coders</span>.
            All Rights Reserved.
          </p>
        </div>
      </div>

      <div className="flex w-full flex-col justify-center bg-gray-900 p-8 md:w-1/2">
        <div className="mx-auto w-full max-w-md">
          <h1 className="mb-2 text-3xl font-bold text-white">
            Distributor Login
          </h1>
          <p className="mb-6 text-sm text-gray-400">
            Log in using your registered email or mobile number
          </p>

          <form className="space-y-6" onSubmit={handleSubmit}>
            <div>
              <label className="mb-2 block text-sm font-medium text-gray-300">
                Login With
              </label>
              <div className="grid grid-cols-2 gap-2">
                <button
                  type="button"
                  onClick={() => {
                    setLoginType("email");
                    setEmailOrMobile("");
                    setError("");
                  }}
                  className={`rounded-lg border px-4 py-3 text-sm font-medium transition ${
                    loginType === "email"
                      ? "border-blue-600 bg-blue-600 text-white"
                      : "border-gray-700 bg-gray-800 text-gray-300"
                  }`}
                >
                  Email
                </button>

                <button
                  type="button"
                  onClick={() => {
                    setLoginType("mobile");
                    setEmailOrMobile("");
                    setError("");
                  }}
                  className={`rounded-lg border px-4 py-3 text-sm font-medium transition ${
                    loginType === "mobile"
                      ? "border-blue-600 bg-blue-600 text-white"
                      : "border-gray-700 bg-gray-800 text-gray-300"
                  }`}
                >
                  Mobile
                </button>
              </div>
            </div>

            <div>
              <label
                htmlFor="emailOrMobile"
                className="mb-2 block text-sm font-medium text-gray-300"
              >
                {loginType === "email" ? "Email" : "Mobile Number"}
              </label>

              <div className="relative">
                <input
                  id="emailOrMobile"
                  type={loginType === "email" ? "email" : "text"}
                  inputMode={loginType === "mobile" ? "numeric" : "email"}
                  placeholder={
                    loginType === "email"
                      ? "Enter your email"
                      : "Enter your mobile number"
                  }
                  value={emailOrMobile}
                  onChange={(e) => {
                    const value = e.target.value;
                    setEmailOrMobile(
                      loginType === "mobile" ? value.replace(/\D/g, "") : value,
                    );
                  }}
                  className="block w-full rounded-lg border border-gray-700 bg-gray-800 px-4 py-3 pr-11 text-gray-100 shadow-sm focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-400 sm:text-sm"
                  required
                />

                <span className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-400">
                  {loginType === "email" ? <FaEnvelope /> : <FaPhone />}
                </span>
              </div>
            </div>

            <div>
              <label
                htmlFor="password"
                className="mb-2 block text-sm font-medium text-gray-300"
              >
                Password
              </label>

              <div className="relative">
                <input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  placeholder="Enter your password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="block w-full rounded-lg border border-gray-700 bg-gray-800 px-4 py-3 pr-20 text-gray-100 shadow-sm focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-400 sm:text-sm"
                  required
                />

                <span className="absolute inset-y-0 right-10 flex items-center pr-2 text-gray-400">
                  <FaLock />
                </span>

                <button
                  type="button"
                  onClick={() => setShowPassword((prev) => !prev)}
                  className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-400"
                >
                  {showPassword ? <FaEyeSlash /> : <FaEye />}
                </button>
              </div>
            </div>

            {error ? (
              <p className="text-center text-sm text-red-400">{error}</p>
            ) : null}

            <div className="mt-8">
              <button
                type="submit"
                className="w-full rounded-lg bg-gradient-to-r from-blue-600 to-pink-600 px-4 py-3 text-white shadow-md hover:from-blue-700 hover:to-pink-700 focus:outline-none focus:ring-2 focus:ring-blue-400 focus:ring-offset-2 disabled:opacity-60"
                disabled={loading}
              >
                {loading ? "Logging in..." : "Log In"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default Login;
